// Returns & Quality dashboard — surfaces the ~$42M/yr of return credits that
// were invisible until now. Simple on the surface (one page: KPIs → trend →
// offenders) but every number is a full ClickHouse aggregation over 3.96M
// return lines joined to 9.75M sales lines, so rates and net revenue are real.
//
// Backend: ReturnsDashboardController  (api/ReturnsDashboard/*)
// Semantics: units returned = ReceivedQty · credited $ = ExtPrice on return
// lines · OSR = one-step return, CR = credit return · return rate =
// units returned ÷ units sold in the same window · net = sales $ − credit $.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, IconButton, TextField, MenuItem,
  ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip, CircularProgress,
  Skeleton, Alert, InputAdornment, Tabs, Tab, Table, TableHead, TableBody,
  TableRow, TableCell, Select,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MdAssignmentReturn, MdAttachMoney, MdTrendingDown, MdPercent, MdPeople,
  MdRefresh, MdFileDownload, MdArrowUpward, MdArrowDownward, MdSearch,
  MdFilterListOff, MdShowChart,
} from 'react-icons/md';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip,
} from 'recharts';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS, apiCall } from '../config/api';

// returns = cost, so the bars wear the palette's warm alarm hue; rate stays blue
const CREDIT_COLOR = '#D55E00';
const RATE_COLOR = '#0072B2';

const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const usdC = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });
const num0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const numC = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const money = (n, compact = false) => (compact ? usdC : usd0).format(Number(n) || 0);
const count = (n, compact = false) => (compact ? numC : num0).format(Number(n) || 0);
const pct1 = (n) => `${((Number(n) || 0) * 100).toFixed(1)}%`;

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const PRESETS = [
  { key: '30d', label: '30D', days: 30 },
  { key: '90d', label: '90D', days: 90 },
  { key: '6mo', label: '6M', months: 6 },
  { key: '12mo', label: '12M', months: 12 },
  { key: 'ytd', label: 'YTD', ytd: true },
];

const OFFENDER_TABS = [
  { key: 'sku', label: 'Top SKUs', file: 'Returns_Top_SKUs' },
  { key: 'collection', label: 'Collections', file: 'Returns_Collections' },
  { key: 'customer', label: 'Customers', file: 'Returns_Customers' },
];

// minimum units sold before a return RATE is trustworthy enough to rank by
const RATE_MIN_SOLD = 30;

// ---- small pieces ------------------------------------------------------------
// For returns, "up" is bad — deltas are colored inverted (more returns = red).
function KpiTile({ icon: Icon, label, value, prev, kind = 'money', invert = false, hint }) {
  const theme = useTheme();
  // null value = not computable (e.g. a rate with zero sales in window) → em dash, no delta
  const na = value == null;
  const cur = na ? 0 : Number(value) || 0;
  const p = na || prev == null ? null : Number(prev) || 0;
  const delta = p != null && p !== 0 ? (cur - p) / Math.abs(p) : null;
  const up = delta != null && delta >= 0;
  const good = invert ? !up : up;
  const deltaColor = delta == null ? theme.palette.text.disabled : good ? theme.palette.success.main : theme.palette.error.main;
  const display = na ? '—' : kind === 'money' ? money(cur, true) : kind === 'pct' ? pct1(cur) : count(cur);
  return (
    <Paper elevation={0} sx={{
      p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`,
      display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0,
    }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 30, height: 30, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: CREDIT_COLOR + '1a', color: CREDIT_COLOR, flexShrink: 0 }}>
          <Icon size={17} />
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: .3, textTransform: 'uppercase', fontSize: 11 }} noWrap>{label}</Typography>
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }} noWrap>{display}</Typography>
      {delta != null ? (
        <Stack direction="row" alignItems="center" spacing={0.4} sx={{ color: deltaColor }}>
          {up ? <MdArrowUpward size={14} /> : <MdArrowDownward size={14} />}
          <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{Math.abs(delta * 100).toFixed(1)}%</Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>vs prev</Typography>
        </Stack>
      ) : (
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>{hint || 'current period'}</Typography>
      )}
    </Paper>
  );
}

const rateOf = (r) => {
  const sold = Number(r.unitsSold) || 0;
  if (sold <= 0) return null;
  return (Number(r.unitsReturned) || 0) / sold;
};

function RateChip({ r }) {
  const rate = rateOf(r);
  if (rate === null) return <Chip label="no sales" size="small" sx={{ height: 20, fontSize: 10.5 }} />;
  const color = rate >= 0.15 ? '#c62828' : rate >= 0.07 ? '#b26a00' : '#2e7d32';
  return (
    <Chip label={pct1(rate)} size="small"
      sx={{ height: 20, fontSize: 11, fontWeight: 700, color: '#fff', bgcolor: color, fontVariantNumeric: 'tabular-nums' }} />
  );
}

// =============================================================================
export default function ReturnsDashboard() {
  const theme = useTheme();

  const [filters, setFilters] = useState(() => ({
    preset: '12mo',
    from: iso(addMonths(new Date(), -12)),
    to: iso(new Date()),
    type: 'all',
    customer: '',
    collection: '',
  }));
  const [custText, setCustText] = useState('');
  const [collText, setCollText] = useState('');

  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [offenders, setOffenders] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(true);
  const [error, setError] = useState('');
  const [topN, setTopN] = useState(50);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('credit');
  const [sortDir, setSortDir] = useState('desc');
  const [refreshTick, setRefreshTick] = useState(0); // Refresh must refetch offenders too
  const abortRef = useRef(null);

  const grain = useMemo(() => {
    const days = (new Date(filters.to) - new Date(filters.from)) / 86400000;
    return days <= 92 ? 'day' : 'month';
  }, [filters.from, filters.to]);

  const qs = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    p.set('from', filters.from); p.set('to', filters.to);
    if (filters.type !== 'all') p.set('type', filters.type);
    if (filters.customer) p.set('customer', filters.customer);
    if (filters.collection) p.set('collection', filters.collection);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  }, [filters]);

  // summary + trend
  const loadCore = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true); setError('');
    const settled = await Promise.allSettled([
      apiCall(`${API_ENDPOINTS.RETURNS_DASH_SUMMARY}?${qs()}`, { signal: ac.signal }),
      apiCall(`${API_ENDPOINTS.RETURNS_DASH_TREND}?${qs({ grain })}`, { signal: ac.signal }),
    ]);
    if (abortRef.current !== ac) return;
    const [s, t] = settled;
    if (s.status === 'fulfilled' && s.value) setSummary(s.value);
    if (t.status === 'fulfilled' && t.value) setTrend((t.value && t.value.data) || []);
    const failed = settled.filter((x) => x.status === 'rejected' && x.reason?.name !== 'AbortError');
    if (failed.length) setError(failed[0].reason?.message || 'Failed to load');
    setLoading(false);
  }, [qs, grain]);

  useEffect(() => { loadCore(); }, [loadCore]);

  // offenders — only the visible tab. Rows are cleared up-front so the previous
  // dimension's data never renders under the new tab's headers.
  useEffect(() => {
    let live = true;
    setTabLoading(true);
    setOffenders([]);
    apiCall(`${API_ENDPOINTS.RETURNS_DASH_REPORT}?${qs({ dim: OFFENDER_TABS[tab].key, limit: 5000 })}`)
      .then((r) => { if (live) { setOffenders((r && r.data) || []); setTabLoading(false); } })
      .catch((e) => { if (live && e.name !== 'AbortError') { setError(e.message || 'Failed to load offenders'); setTabLoading(false); } });
    return () => { live = false; };
  }, [qs, tab, refreshTick]);

  // debounced text filters
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.customer === custText.trim() ? f : { ...f, customer: custText.trim() })), 450);
    return () => clearTimeout(t);
  }, [custText]);
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.collection === collText.trim() ? f : { ...f, collection: collText.trim() })), 450);
    return () => clearTimeout(t);
  }, [collText]);

  // reset per-tab table state
  useEffect(() => { setSearch(''); setSortKey('credit'); setSortDir('desc'); }, [tab]);

  const applyPreset = (key) => {
    const now = new Date();
    const preset = PRESETS.find((p) => p.key === key);
    let from;
    if (preset?.ytd) from = new Date(now.getFullYear(), 0, 1);
    else if (preset?.months) from = addMonths(now, -preset.months);
    else from = addDays(now, -(preset?.days ?? 90));
    setFilters((f) => ({ ...f, preset: key, from: iso(from), to: iso(now) }));
  };
  const setField = (k, v) => setFilters((f) => ({ ...f, [k]: v, preset: k === 'from' || k === 'to' ? 'custom' : f.preset }));
  const clearFilters = () => {
    setCustText(''); setCollText('');
    setFilters({ preset: '12mo', from: iso(addMonths(new Date(), -12)), to: iso(new Date()), type: 'all', customer: '', collection: '' });
  };

  // ---- derived KPI values -----------------------------------------------------
  const R = summary?.returns || {};
  const S = summary?.sales || {};
  const rateUnits = Number(S.units) > 0 ? (Number(R.units) || 0) / Number(S.units) : null;
  const prevRateUnits = Number(S.prevUnits) > 0 ? (Number(R.prevUnits) || 0) / Number(S.prevUnits) : null;
  const rateDollars = Number(S.revenue) > 0 ? (Number(R.credit) || 0) / Number(S.revenue) : null;
  const net = (Number(S.revenue) || 0) - (Number(R.credit) || 0);
  const prevNet = (Number(S.prevRevenue) || 0) - (Number(R.prevCredit) || 0);
  const typeTotal = (Number(R.osrCredit) || 0) + (Number(R.crCredit) || 0);

  // ---- offenders table --------------------------------------------------------
  const dimKey = OFFENDER_TABS[tab].key;
  const netOf = (r) => (Number(r.revenue) || 0) - (Number(r.credit) || 0);
  const columns = useMemo(() => {
    const base = dimKey === 'sku'
      ? [
        { key: 'x', label: 'SKU', align: 'left', cell: (r) => r.x || '' },
        { key: 'collection', label: 'Collection', align: 'left', cell: (r) => r.collection || '' },
      ]
      : dimKey === 'customer'
        ? [{ key: 'x', label: 'Customer', align: 'left', cell: (r) => (r.name ? `${r.name} (${r.x})` : r.x || '') }]
        : [{ key: 'x', label: 'Collection', align: 'left', cell: (r) => r.x || '' }];
    return [
      ...base,
      { key: 'unitsReturned', label: 'Units Ret.', align: 'right', sortable: true, raw: (r) => Number(r.unitsReturned) || 0, cell: (r) => count(r.unitsReturned) },
      { key: 'credit', label: 'Credited $', align: 'right', sortable: true, raw: (r) => Number(r.credit) || 0, cell: (r) => money(r.credit) },
      { key: 'unitsSold', label: 'Units Sold', align: 'right', sortable: true, raw: (r) => Number(r.unitsSold) || 0, cell: (r) => count(r.unitsSold) },
      { key: 'rate', label: 'Return Rate', align: 'right', sortable: true, raw: (r) => rateOf(r) ?? -1, cell: null /* chip */ },
      { key: 'net', label: 'Net $', align: 'right', sortable: true, raw: netOf, cell: (r) => money(netOf(r)) },
    ];
  }, [dimKey]);

  const displayed = useMemo(() => {
    let out = [...offenders];
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      out = out.filter((r) => `${r.x || ''} ${r.collection || ''} ${r.name || ''}`.toLowerCase().includes(s));
    }
    // ranking by rate is only meaningful with a sales floor
    if (sortKey === 'rate') out = out.filter((r) => (Number(r.unitsSold) || 0) >= RATE_MIN_SOLD);
    const col = columns.find((c) => c.key === sortKey);
    if (col && col.raw) {
      out.sort((a, b) => (sortDir === 'asc' ? col.raw(a) - col.raw(b) : col.raw(b) - col.raw(a)));
    }
    return out.slice(0, topN);
  }, [offenders, search, sortKey, sortDir, topN, columns]);

  // ---- export ------------------------------------------------------------------
  const exportExcel = () => {
    // numeric columns export as raw numbers (not locale strings) so Excel can
    // sum/sort them; the rate exports as a 0-1 fraction
    const head = ['#', ...columns.map((c) => c.label)];
    const body = displayed.map((r, i) => [i + 1, ...columns.map((c) => {
      if (c.key === 'rate') { const rate = rateOf(r); return rate === null ? 'no sales' : Number(rate.toFixed(4)); }
      if (c.raw) return c.raw(r);
      return String(c.cell(r));
    })]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...body]), OFFENDER_TABS[tab].label.slice(0, 31));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{
      From: filters.from, To: filters.to, Type: filters.type,
      Customer: filters.customer || '(all)', Collection: filters.collection || '(all)',
      CreditedDollars: Number(R.credit) || 0, UnitsReturned: Number(R.units) || 0,
      ReturnLines: Number(R.lines) || 0, SalesRevenue: Number(S.revenue) || 0,
      SalesUnits: Number(S.units) || 0,
      ReturnRateUnits: rateUnits, ReturnRateDollars: rateDollars, NetRevenue: net,
    }]), 'Summary');
    XLSX.writeFile(wb, `${OFFENDER_TABS[tab].file}_${filters.from}_to_${filters.to}.xlsx`);
  };

  const fmtPeriod = useCallback((v) => {
    if (!v) return '';
    // date-only strings parse as UTC midnight — format in UTC too, or every
    // bucket renders one period early for US-timezone viewers
    const d = new Date(v);
    return grain === 'day'
      ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
      : d.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  }, [grain]);

  const gridColor = theme.palette.divider;
  const axisColor = theme.palette.text.secondary;
  const chartTooltipStyle = { background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 10, fontSize: 13 };

  // =============================================================================
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      {/* header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MdAssignmentReturn size={26} color={CREDIT_COLOR} />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Returns & Quality</Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Credited returns vs sales, return rates and top offenders · live from the warehouse
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<MdFileDownload />} onClick={exportExcel} disabled={tabLoading || displayed.length === 0}>Export</Button>
          <Button size="small" variant="contained" startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <MdRefresh />} onClick={() => { setRefreshTick((t) => t + 1); loadCore(); }} disabled={loading}>Refresh</Button>
        </Stack>
      </Stack>

      {/* filter bar */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5}>
          <ToggleButtonGroup size="small" exclusive value={filters.preset} onChange={(e, v) => v && applyPreset(v)}>
            {PRESETS.map((p) => <ToggleButton key={p.key} value={p.key} sx={{ px: 1.2, fontWeight: 700 }}>{p.label}</ToggleButton>)}
          </ToggleButtonGroup>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => setField('from', e.target.value)} sx={{ width: 150 }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => setField('to', e.target.value)} sx={{ width: 150 }} />
          <TextField size="small" select label="Return type" value={filters.type} onChange={(e) => setField('type', e.target.value)} sx={{ width: 190 }}>
            <MenuItem value="all">All returns</MenuItem>
            <MenuItem value="osr">One-step (OSR)</MenuItem>
            <MenuItem value="cr">Credit return (CR)</MenuItem>
          </TextField>
          <TextField size="small" placeholder="Customer…" value={custText} onChange={(e) => setCustText(e.target.value)} sx={{ width: 180 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }} />
          <TextField size="small" placeholder="Collection…" value={collText} onChange={(e) => setCollText(e.target.value)} sx={{ width: 180 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }} />
          {(filters.type !== 'all' || filters.customer || filters.collection) && (
            <MuiTooltip title="Clear all filters">
              <IconButton size="small" onClick={clearFilters}><MdFilterListOff /></IconButton>
            </MuiTooltip>
          )}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* KPI row — up is bad here, so deltas are inverted */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', lg: 'repeat(6,1fr)' }, gap: 1.5, mb: 2 }}>
        {loading && !summary
          ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rounded" height={104} sx={{ borderRadius: 3 }} />)
          : (<>
            <KpiTile icon={MdAttachMoney} label="Credited $" value={R.credit} prev={R.prevCredit} kind="money" invert />
            <KpiTile icon={MdAssignmentReturn} label="Units Returned" value={R.units} prev={R.prevUnits} kind="count" invert />
            <KpiTile icon={MdPercent} label="Return Rate (units)" value={rateUnits} prev={prevRateUnits} kind="pct" invert />
            <KpiTile icon={MdPercent} label="Return Rate ($)" value={rateDollars} kind="pct" hint="credit ÷ sales revenue" />
            <KpiTile icon={MdTrendingDown} label="Net Revenue" value={net} prev={prevNet} kind="money" />
            <KpiTile icon={MdPeople} label="Customers Returning" value={R.customers} kind="count" hint="in window" />
          </>)}
      </Box>

      {/* OSR vs CR split */}
      {typeTotal > 0 && (
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2, mb: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
            Credited $ by return type
          </Typography>
          <Box sx={{ display: 'flex', borderRadius: 1, overflow: 'hidden', height: 24 }}>
            {[{ label: 'One-step (OSR)', v: Number(R.osrCredit) || 0, c: CREDIT_COLOR },
              { label: 'Credit return (CR)', v: Number(R.crCredit) || 0, c: RATE_COLOR }].map((seg) => {
              const p = (seg.v / typeTotal) * 100;
              if (seg.v <= 0) return null;
              return (
                <MuiTooltip key={seg.label} title={`${seg.label}: ${money(seg.v)} (${p.toFixed(1)}%)`}>
                  <Box sx={{ width: `${p}%`, bgcolor: seg.c, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 46 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>{p.toFixed(1)}%</Typography>
                  </Box>
                </MuiTooltip>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>One-step (OSR): {money(R.osrCredit)}</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Credit return (CR): {money(R.crCredit)}</Typography>
          </Box>
        </Paper>
      )}

      {/* trend: credited $ bars + return-rate line, stacked panels sharing the x-axis */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <MdShowChart size={18} color={CREDIT_COLOR} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Returns trend</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {grain === 'day' ? 'daily' : 'monthly'} · bars = credited $ · line = return rate (units returned ÷ units sold)
          </Typography>
        </Stack>
        {loading && !trend.length ? <Skeleton variant="rounded" height={300} /> : !trend.length ? (
          <Box sx={{ height: 220, display: 'grid', placeItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>No returns in this range</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ height: 190 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 4, right: 12, bottom: 0, left: 4 }} syncId="ret">
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                  <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fill: axisColor, fontSize: 11 }} tickLine={false} axisLine={{ stroke: gridColor }} minTickGap={24} />
                  <YAxis tickFormatter={(v) => money(v, true)} tick={{ fill: axisColor, fontSize: 11 }} tickLine={false} axisLine={false} width={56} />
                  <Tooltip contentStyle={chartTooltipStyle} labelFormatter={fmtPeriod}
                    formatter={(v, n) => [money(v), n === 'credit' ? 'Credited' : n]} />
                  <Bar dataKey="credit" name="credit" fill={CREDIT_COLOR} radius={[3, 3, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ height: 150 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 4, right: 12, bottom: 0, left: 4 }} syncId="ret">
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                  <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fill: axisColor, fontSize: 11 }} tickLine={false} axisLine={{ stroke: gridColor }} minTickGap={24} />
                  <YAxis tickFormatter={(v) => pct1(v)} tick={{ fill: axisColor, fontSize: 11 }} tickLine={false} axisLine={false} width={56} />
                  <Tooltip contentStyle={chartTooltipStyle} labelFormatter={fmtPeriod}
                    formatter={(v) => [v == null ? '—' : pct1(v), 'Return rate']} />
                  <Line type="monotone" dataKey="rateUnits" name="rate" stroke={RATE_COLOR} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}
      </Paper>

      {/* offenders */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: `1px solid ${theme.palette.divider}`, minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600, fontSize: 13 } }}>
          {OFFENDER_TABS.map((t) => <Tab key={t.key} label={t.label} />)}
        </Tabs>
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Filter rows…" value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }} sx={{ width: 220 }} />
          <Select size="small" value={topN} onChange={(e) => setTopN(e.target.value)} sx={{ fontSize: 12, minWidth: 90 }}>
            {[25, 50, 100, 250].map((n) => <MenuItem key={n} value={n}>Top {n}</MenuItem>)}
          </Select>
          {sortKey === 'rate' && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              rate ranking requires ≥ {RATE_MIN_SOLD} units sold{offenders.length >= 5000 ? ' · list capped at the top 5,000 by credited $' : ''}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>sorted by {columns.find((c) => c.key === sortKey)?.label || 'Credited $'}</Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: CREDIT_COLOR, color: '#fff', fontWeight: 600, fontSize: 12, py: '10px', px: '14px' }}>#</TableCell>
                {columns.map((c) => (
                  <TableCell key={c.key} align={c.align}
                    onClick={c.sortable ? () => {
                      if (sortKey === c.key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
                      else { setSortKey(c.key); setSortDir('desc'); }
                    } : undefined}
                    sx={{ bgcolor: CREDIT_COLOR, color: '#fff', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', py: '10px', px: '14px', cursor: c.sortable ? 'pointer' : 'default', userSelect: 'none' }}>
                    {c.label}
                    {c.sortable && sortKey === c.key && <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                    {c.sortable && sortKey !== c.key && <span style={{ marginLeft: 4, opacity: 0.4 }}>{'⇅'}</span>}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tabLoading && offenders.length === 0 ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={columns.length + 1}><Skeleton height={22} /></TableCell></TableRow>
                ))
              ) : displayed.length === 0 ? (
                <TableRow><TableCell colSpan={columns.length + 1} sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>No returns found</TableCell></TableRow>
              ) : (
                displayed.map((r, idx) => (
                  <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { bgcolor: theme.palette.action.hover } }}>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600, fontSize: 13, px: '14px', py: '8px' }}>{idx + 1}</TableCell>
                    {columns.map((c) => (
                      <TableCell key={c.key} align={c.align} sx={{ fontSize: 13, px: '14px', py: '8px', fontVariantNumeric: 'tabular-nums' }}>
                        {c.key === 'rate' ? <RateChip r={r} /> : String(c.cell(r))}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 2, textAlign: 'center' }}>
        Credited $ = ExtPrice on return lines (RPTCustomerReturnswithOneStepCredit) · units = ReceivedQty · return rate = units returned ÷ units sold in the same window · net = sales revenue − credited $ · OSR = one-step return, CR = credit return
      </Typography>
    </Box>
  );
}
